import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
	ColumnFilter,
	convertDateRangeFiltersFromStringsToDates,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import {
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_SIZE,
} from '@dealstack/frontend/shared/components/Table/MantineTable/constants';
import { UpdateURLParam, updateURLParam } from '@dealstack/frontend/shared/helper/updateUrlParams';
import { useIsFiltersApplied } from './hooks/useIsFiltersApplied';
import { QueryScopePart, useResetPageOnQueryChange } from './hooks/useResetPageOnQueryChange';

enum PARAM_KEYS {
	GLOBAL_SEARCH = 'globalSearch',
	FILTERS = 'filters',
	PAGE_INDEX = 'pageIndex',
	PAGE_SIZE = 'pageSize',
	SORT = 'sort',
	QUICK_FILTERS = 'quickFilters',
	SHOW_FILTERS = 'showFilters',
}

export const buildTableFiltersSearch = (filters: ColumnFiltersState): string =>
	`${PARAM_KEYS.FILTERS}=${encodeURIComponent(JSON.stringify(filters))}`;

export const buildTableQuickFiltersSearch = (
	quickFilters: Record<string, boolean | string>
): string => `${PARAM_KEYS.QUICK_FILTERS}=${encodeURIComponent(JSON.stringify(quickFilters))}`;

const defaultPagination: PaginationState = {
	[PARAM_KEYS.PAGE_INDEX]: DEFAULT_PAGE_INDEX,
	[PARAM_KEYS.PAGE_SIZE]: DEFAULT_PAGE_SIZE,
};

// A malformed param (e.g. ?pageSize=abc) falls back rather than poisoning state with NaN.
const parsePaginationParam = (param: string | null, fallback: number): number => {
	const parsed = Number.parseInt(param ?? '', 10);
	return Number.isInteger(parsed) && parsed >= 0 ? parsed : fallback;
};

const parseJsonParam = <T>(param: string | null, fallback: T): T => {
	if (!param) {
		return fallback;
	}

	try {
		return JSON.parse(param) as T;
	} catch {
		return fallback;
	}
};

interface ParamState {
	columnFilters: ColumnFiltersState;
	globalSearchString: string;
	sorting: SortingState;
	pagination: PaginationState;
	quickFilters: Record<string, boolean | string>;
}

const readFilters = (params: URLSearchParams, fallback: ColumnFiltersState) => {
	const param = params.get(PARAM_KEYS.FILTERS);

	return param
		? convertDateRangeFiltersFromStringsToDates(parseJsonParam<ColumnFiltersState>(param, []))
		: fallback;
};

/**
 * The params this hook owns. Other components put their own keys on the same url — the filters
 * button writes `showFilters` — and those must not look like a navigation to it.
 */
const OWNED_PARAM_KEYS = [
	PARAM_KEYS.FILTERS,
	PARAM_KEYS.GLOBAL_SEARCH,
	PARAM_KEYS.SORT,
	PARAM_KEYS.PAGE_INDEX,
	PARAM_KEYS.PAGE_SIZE,
	PARAM_KEYS.QUICK_FILTERS,
];

const ownedSignature = (params: URLSearchParams): string =>
	OWNED_PARAM_KEYS.map((key) => `${key}=${params.get(key) ?? ''}`).join('&');

const readParamState = (params: URLSearchParams, defaults: ParamState): ParamState => ({
	columnFilters: readFilters(params, defaults.columnFilters),
	globalSearchString: params.get(PARAM_KEYS.GLOBAL_SEARCH) || defaults.globalSearchString,
	sorting: parseJsonParam<SortingState>(params.get(PARAM_KEYS.SORT), defaults.sorting),
	pagination: {
		pageIndex: parsePaginationParam(
			params.get(PARAM_KEYS.PAGE_INDEX),
			defaults.pagination.pageIndex
		),
		pageSize: parsePaginationParam(
			params.get(PARAM_KEYS.PAGE_SIZE),
			defaults.pagination.pageSize
		),
	},
	quickFilters: parseJsonParam(params.get(PARAM_KEYS.QUICK_FILTERS), defaults.quickFilters),
});

interface Props {
	initialColumnFilters?: ColumnFiltersState;
	initialGlobalSearchString?: string;
	initialShowGlobalSearchStringInput?: boolean;
	initialSorting?: SortingState;
	initialPagination?: PaginationState;
	initialQuickFilters?: Record<string, boolean | string>;
	initialShowFilters?: boolean;
	queryScope?: readonly QueryScopePart[];
}

export type UseMantineTableDataOperationsPartialProps = Partial<Props>;

export const useMantineTableDataOperationsFromUrlParamState = (
	props: UseMantineTableDataOperationsPartialProps = {}
) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const {
		initialColumnFilters = [],
		initialGlobalSearchString = '',
		initialShowGlobalSearchStringInput = false,
		initialSorting = [],
		initialPagination = defaultPagination,
		initialQuickFilters = {},
		queryScope,
	} = props;

	const defaults: ParamState = {
		columnFilters: initialColumnFilters,
		globalSearchString: initialGlobalSearchString,
		sorting: initialSorting,
		pagination: initialPagination,
		quickFilters: initialQuickFilters,
	};
	const defaultsRef = useRef(defaults);
	const [initialState] = useState(() => readParamState(searchParams, defaults));

	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
		initialState.columnFilters
	);
	const [globalSearchString, setGlobalSearchString] = useState<string>(
		initialState.globalSearchString
	);
	const [showGlobalSearchStringInput, setShowGlobalSearchStringInput] = useState(
		initialShowGlobalSearchStringInput
	);
	const [sorting, setSorting] = useState<SortingState>(initialState.sorting);
	const [pagination, setPagination] = useState<PaginationState>(initialState.pagination);
	const [quickFilters, setQuickFilters] = useState<Record<string, boolean | string>>(
		initialState.quickFilters
	);

	// What this hook last put in the URL. Anything else there arrived by navigation.
	const writtenParams = useRef(ownedSignature(searchParams));

	// queryScope is for filters this hook doesn't manage, such as a bulk-action mode.
	// Runs before the URL-sync effect below so a corrected page index is what gets written.
	useResetPageOnQueryChange(
		[columnFilters, globalSearchString, quickFilters, queryScope],
		pagination,
		setPagination
	);

	useEffect(() => {
		const newParams = new URLSearchParams(searchParams);

		// ignore mantine table default filter which always sets the date-range in the urls as [null, null]
		const isEmptyDateRange = (f: ColumnFilter) =>
			Array.isArray(f.value) && f.value.length === 2 && f.value.every((v) => v == null);

		// clean up filters
		const cleanedFilters = columnFilters.filter((f) => !isEmptyDateRange(f));

		const updateParams: UpdateURLParam[] = [
			{
				key: PARAM_KEYS.FILTERS,
				condition: cleanedFilters.length > 0,
				value: JSON.stringify(cleanedFilters),
				newParams,
			},
			{
				key: PARAM_KEYS.GLOBAL_SEARCH,
				condition: !!globalSearchString,
				value: globalSearchString,
				newParams,
			},
			{
				key: PARAM_KEYS.SORT,
				condition: sorting.length > 0,
				value: JSON.stringify(sorting),
				newParams,
			},
			{
				key: PARAM_KEYS.PAGE_INDEX,
				condition: pagination.pageIndex !== DEFAULT_PAGE_INDEX,
				value: pagination.pageIndex.toString(),
				newParams,
			},
			{
				key: PARAM_KEYS.PAGE_SIZE,
				condition: pagination.pageSize !== DEFAULT_PAGE_SIZE,
				value: pagination.pageSize.toString(),
				newParams,
			},
			{
				key: PARAM_KEYS.QUICK_FILTERS,
				condition:
					Object.keys(quickFilters).length > 0 ||
					Object.keys(initialQuickFilters).length > 0,
				value: JSON.stringify(quickFilters),
				newParams,
			},
		];

		updateParams.forEach(updateURLParam);

		const oldParams = searchParams.toString();
		const newParamsString = newParams.toString();

		writtenParams.current = ownedSignature(newParams);

		if (oldParams !== newParamsString) {
			setSearchParams(newParams, { replace: true });
		}

		// search params are set in this effect, so we don't want to add it as a dependency
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		columnFilters,
		globalSearchString,
		sorting,
		pagination.pageIndex,
		pagination.pageSize,
		quickFilters,
	]);

	// Back, forward or a pasted link changes the url without going through the setters above, and
	// the state would otherwise keep its old values and write them straight back over it.
	useEffect(() => {
		const current = ownedSignature(searchParams);

		if (current === writtenParams.current) {
			return;
		}

		writtenParams.current = current;

		const next = readParamState(searchParams, defaultsRef.current);

		setColumnFilters(next.columnFilters);
		setGlobalSearchString(next.globalSearchString);
		setSorting(next.sorting);
		setPagination(next.pagination);
		setQuickFilters(next.quickFilters);
	}, [searchParams]);

	const resetAll = useCallback(() => {
		setColumnFilters([]);
		setGlobalSearchString('');
		setShowGlobalSearchStringInput(false);
		setSorting([]);
		setPagination(initialPagination);
		setQuickFilters({});
		setSearchParams({}); // clear all URL params
	}, [initialPagination, setSearchParams]);

	const isFiltersApplied = useIsFiltersApplied(columnFilters, globalSearchString);

	return {
		columnFilters,
		setColumnFilters,
		globalSearchString,
		setGlobalSearchString,
		sorting,
		setSorting,
		pagination,
		setPagination,
		showGlobalSearchStringInput,
		setShowGlobalSearchStringInput,
		isFiltersApplied,
		resetAll,
		quickFilters,
		setQuickFilters,
	};
};
export type MantineTableDataOperations = ReturnType<
	typeof useMantineTableDataOperationsFromUrlParamState
>;
